import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../root';

export const COMMON_FEATURE_KEY = 'commonData';

export interface CommonDataState {
  selectedItemIds: (string | number)[];
  selectedItemsMap: Record<any, any>;
  isSelectedAll: boolean;
}

export const initialCommonDataState: CommonDataState = {
  selectedItemIds: [],
  selectedItemsMap: {},
  isSelectedAll: false,
};

export const commonDataSlice = createSlice({
  name: COMMON_FEATURE_KEY,
  initialState: initialCommonDataState,
  reducers: {
    updateSelectedItemIds: (state, action: PayloadAction<string | number>) => {
      const id = action.payload;
      const index = state.selectedItemIds.indexOf(id);

      if (index > -1) {
        state.selectedItemIds.splice(index, 1);
      } else {
        state.selectedItemIds.push(id);
      }
    },
    updateSelectedItemsMap: (state, action: PayloadAction<any>) => {
      if (Array.isArray(action.payload)) {
        state.selectedItemIds = action.payload.map((item: any) => item.id);
        state.selectedItemsMap = action.payload.reduce(
          (acc: any, item: any) => {
            acc[item.id] = item;
            return acc;
          },
          {}
        );
        return;
      }

      const item = action.payload;
      const itemId = item.id;

      const alreadySelected = state.selectedItemIds.includes(itemId);

      if (alreadySelected) {
        state.selectedItemIds = state.selectedItemIds.filter(
          (id) => id !== itemId
        );
        delete state.selectedItemsMap[itemId];
      } else {
        state.selectedItemIds.push(itemId);
        state.selectedItemsMap[itemId] = item;
      }
    },
    clearSelectedItemIds: (state) => {
      state.selectedItemIds = [];
    },
    clearSelectedItemsMap: (state) => {
      state.selectedItemsMap = {};
    },
    updateIsSelectedAll: (state, action: PayloadAction<boolean>) => {
      state.isSelectedAll = action.payload;
    },
  },
});

export const commonDataReducer = commonDataSlice.reducer;

export const {
  updateSelectedItemIds,
  updateSelectedItemsMap,
  clearSelectedItemIds,
  clearSelectedItemsMap,
  updateIsSelectedAll,
} = commonDataSlice.actions;

export const getCommonDataSliceState = (
  rootState: RootState
): CommonDataState => rootState[COMMON_FEATURE_KEY];

export const getSelectedItemsIds = createSelector(
  getCommonDataSliceState,
  (state) => state.selectedItemIds
);

export const getSelectedItems = createSelector(
  getCommonDataSliceState,
  (state) => Object.values(state.selectedItemsMap)
);

export const getIsSelectedAll = createSelector(
  getCommonDataSliceState,
  (state) => state.isSelectedAll
);
